import DOMPurify from 'isomorphic-dompurify';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import SortArray from 'array-sort';
import ObjectPath from 'object-path';
import 'dayjs/locale/tr';
import { SOCKET_EVENTS } from '@/constants/socket.ts';

dayjs.extend(relativeTime);
dayjs.locale('tr');

export const jsonTryParse = (arg: string): any | null => {
	let result: unknown = null;

	try {
		result = JSON.parse(arg);
	} catch (e) {
		console.error(e);
	}

	return result;
};

export const jsonTryStringify = (arg: unknown): string | null => {
	let result: string | null = null;

	try {
		result = JSON.stringify(arg);
	} catch (e) {
		console.error(e);
	}

	return result;
};

export const parseJwt = (token: string): Record<string, null> | null => {
	const base64Url: string = token.split('.')[1];
	const base64: string = base64Url?.replace(/-/g, '+')?.replace(/_/g, '/');

	const jsonPayload = decodeURIComponent(
		window
			.atob(base64 || '')
			?.split('')
			.map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
			.join('')
	);

	return jsonTryParse(jsonPayload);
};

export const isNullOrEmptyOrUndefined = (arg: any): boolean => {
	return isUndefined(arg) || isNull(arg) || isEmpty(arg);
};

export const isNullOrUndefined = (arg: any): boolean => {
	return isNull(arg) || isUndefined(arg);
};

export const isBoolean = (arg: any): boolean => {
	return typeof arg === 'boolean';
};

export const isNull = (arg: any): boolean => {
	return arg === null;
};

export const isInfinite = (arg: any): boolean => {
	return !isFinite(arg);
};

export const isEmpty = (arg: any): boolean => {
	if (isObject(arg)) {
		return isEmptyObject(arg);
	}

	if (isArray(arg)) {
		return isEmptyArray(arg);
	}

	return isEmptyString(arg);
};

export const isFalsy = (arg: any): boolean => {
	return arg === false;
};

export const isTruthy = (arg: any): boolean => {
	return arg === true;
};

export const isEmptyString = (arg: any): boolean => {
	return arg === '';
};

export const isEmptyArray = (arg: []): boolean => {
	return Boolean(arg?.length);
};

export const isEmptyObject = (arg: any): boolean => {
	for (const key in arg) {
		if (arg.hasOwnProperty!(key)) {
			return false;
		}
	}

	return true;
};

export const isNumber = (arg: any): boolean => {
	return typeof arg === 'number';
};

export const isString = (arg: any): boolean => {
	return typeof arg === 'string';
};

export const isSymbol = (arg: any): boolean => {
	return typeof arg === 'symbol';
};

export const isUndefined = (arg: any): boolean => {
	return arg === undefined;
};

export const isRegExp = (arg: any): boolean => {
	return arg instanceof RegExp;
};

export const isObject = (arg: any): boolean => {
	return !isNull(arg) && !isDate(arg) && !isRegExp(arg) && typeof arg === 'object';
};

export const isDate = (arg: any): boolean => {
	return arg instanceof Date;
};

export const isFunction = (arg: any): boolean => {
	return typeof arg === 'function';
};

export const isPrimitive = (arg: any): boolean => {
	return isNull(arg) || (!isObject(arg) && !isFunction(arg));
};

export const isArray: any = Array.isArray;

/* Additional tools */
export const assetPath = (filename: string, root?: string): string => {
	if (filename?.includes('://')) return filename;

	return `${root ? root : process.env.ASSET_SERVER_ADDRESS}/${filename}`;
};

export const purify = (html?: string): { __html: string } => {
	return {
		__html: DOMPurify.sanitize(html ?? '', {
			ADD_TAGS: ['iframe'],
			ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target']
		})
	};
};

export const formatRelativeTime = (timestamp: string): string => {
	const now = dayjs();
	const date = dayjs(timestamp);
	const diffInSeconds = now.diff(date, 'second');

	if (diffInSeconds < 60) {
		return 'Az önce';
	}

	return date.fromNow();
};

export const formatCustomDate = (timestamp: string, type?: 'date' | 'time'): string => {
	dayjs.locale('tr');
	const date = dayjs(timestamp);

	if (type === 'date') return date.format('D MMMM YYYY');
	if (type === 'time') return date.format('HH:mm');

	return date.format('D MMMM YYYY, HH:mm');
};

export const removeEmojis = (text: string): string => {
	return text
		.replace(/[\p{Emoji}]/gu, '')
		.replace(/[\uFE0E\uFE0F]/g, '')
		.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '')
		.replace(/:[a-zA-Z0-9_+-]+:|<[/]?3|&lt;[/]?3|♥|❤|[#*0-9]\u20E3/g, '');
};

export const removeLaTeX = (text: string): string => {
	const displayMath = /\$\$[^$]*\$\$/g; // $$ ... $$
	const displayMathBrackets = /\\\[.*?\\\]/g; // \[ ... \]
	const inlineMath = /\$[^$]*\$/g; // $ ... $

	return text.replace(displayMath, '').replace(displayMathBrackets, '').replace(inlineMath, '');
};

export const removeEmojisAndLatex = (text: string): string => {
	text = removeEmojis(text);
	text = removeLaTeX(text);

	return text;
};

export const removeSpaces = (str: string): string => str.replace(/\s+/g, '');

export const formatTime = (seconds: number): string => {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;

	return `${minutes?.toString()?.padStart(2, '0')}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
};

export const optimizeKatexInResponse = (text: string): string => {
	let processed = text.replace(/\\\[([\s\S]*?)\\\]/g, (_, p1) => {
		return `\n\n$$\n${p1.trim()}\n$$\n`;
	});

	processed = processed.replace(/([\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}★])/gu, ' $1 ');

	return processed;
};

export const scrollWithOffset = (el: HTMLElement) => {
	const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
	const yOffset = -90;
	window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
};

export const responseOK = (response: any): boolean => {
	return response?.data?.message === 'Success';
};

export const isVerified = (response: any): boolean => {
	return response?.data?.data?.isVerified;
};

export const validateEmail = (email: string) => {
	const emailRegex = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,6}/;
	return emailRegex.test(email);
};

export const getMonthDateRange = (date: any, startDateFormat = 'YYYY-MM-DDT00:00:00.000+03:00', endDateFormat = 'YYYY-MM-DDT00:00:00.000+03:00') => {
	const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date);

	const startDate = dayjsDate.startOf('month').hour(0).minute(0).second(0).millisecond(0);

	const endDate = dayjsDate.endOf('month').hour(0).minute(0).second(0).millisecond(0);

	return {
		startDate: startDate.format(startDateFormat),
		endDate: endDate.format(endDateFormat)
	};
};

export const formatMilliseconds = (milliseconds: number) => {
	const totalSeconds = Math.floor(milliseconds / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	const formattedMinutes = minutes.toString().padStart(2, '0');
	const formattedSeconds = seconds.toString().padStart(2, '0');

	return `${formattedMinutes}:${formattedSeconds}`;
};

export const formatTimeHHmm = (dateStr?: string): string => {
	if (!dateStr) return '--:--';

	if (/^\d{2}:\d{2}:\d{2}\+\d{2}:\d{2}$/.test(dateStr)) {
		const today = dayjs().tz('Europe/Istanbul').format('YYYY-MM-DD');
		const fullDateStr = `${today}T${dateStr}`;

		return dayjs(fullDateStr).tz('Europe/Istanbul').format('HH:mm');
	}

	return dayjs(dateStr).tz('Europe/Istanbul').format('HH:mm');
};

export const convertMeetingType = (type?: string, def?: string) => {
	const types = {
		INSTANT: 'Anlık',
		APPOINTMENT: 'Randevulu',
		MESSAGING: 'Mesajlaşma'
	};
	if (type && Object.keys(types).includes(type)) {
		return types[type as keyof typeof types];
	} else {
		return types?.[def as keyof typeof types] || types.MESSAGING;
	}
};

export const convertMsToClockTime = (milliseconds: number): { hours: number; minutes: number; seconds: number } => {
	const seconds = Math.floor((milliseconds / 1000) % 60);
	const minutes = Math.floor((milliseconds / (1000 * 60)) % 60);
	const hours = Math.floor(milliseconds / (1000 * 60 * 60));

	return { hours, minutes, seconds };
};

export const convertMsToTimeWithoutSeconds = (milliseconds?: number): { days: number; hours: number; minutes: number } => {
	if (!milliseconds || milliseconds < 0) {
		return { days: 0, hours: 0, minutes: 0 };
	}

	const seconds = Math.ceil(Math.floor((milliseconds / 1000) % 60) / 60);
	const minutes = Math.floor((milliseconds / (1000 * 60)) % 60) + seconds;
	const hours = Math.floor((milliseconds / (1000 * 60 * 60)) % 24);
	const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));

	return { days, hours, minutes };
};

type Path = Array<number | string> | number | string;

interface SortConfig {
	field: Path;
	direction?: 'asc' | 'desc';
}

type SortArg = Path | SortConfig;

export const objectArraySort = <T>(list: T[], path: Path, sortType: 'asc' | 'desc'): T[] => {
	return sortType === 'desc' ? arrayDescSort(list, path) : arrayAscSort(list, path);
};

export const localeSortCompare = (arg: SortArg): ((a: any, b: any) => number) => {
	const isObj = isObject(arg);
	const field: Path = isObj ? (arg as SortConfig).field : (arg as Path);
	const isReverse = isObj && (arg as SortConfig).direction === 'desc';

	return (a: any, b: any): number => {
		const first = ObjectPath.get(a, field);
		const second = ObjectPath.get(b, field);

		return isString(first) || isString(second)
			? String((isReverse ? second : first) || '').localeCompare(String((isReverse ? first : second) || ''))
			: ((isReverse ? second : first) || 0) - ((isReverse ? first : second) || 0);
	};
};

export const localeSort = (arg: SortArg | SortArg[]): ((a: any, b: any) => number) | ((a: any, b: any) => number)[] => {
	if (isArray(arg)) {
		const argList: ((a: any, b: any) => number) | ((a: any, b: any) => number)[] = [];

		const argArray = arg as SortArg[];

		argArray.forEach((item: SortArg) => {
			argList.push(localeSortCompare(item));
		});

		return argList;
	}

	return localeSortCompare(arg as SortArg);
};

export const arrayAscSort = <T>(list: T[], arg: Path): T[] => {
	return SortArray(list, localeSort(arg));
};

export const arrayDescSort = <T>(list: T[], arg: Path): T[] => {
	return SortArray(list, localeSort(arg), { reverse: true });
};

export const convertMsToHoursFloat = (milliseconds?: number): number => {
	if (!milliseconds || milliseconds < 0) {
		return 0;
	}

	return milliseconds / (1000 * 60 * 60);
};

/**
 * Checks if it is within the time range.
 */
export function isNowInRange({ nowTime, startTime, endTime }): boolean {
	let baseTime: dayjs.Dayjs | undefined;

	if (nowTime) {
		baseTime = dayjs(nowTime);
	} else {
		return false;
	}

	const today = baseTime.format('YYYY-MM-DD');
	const formattedStart = dayjs(`${today}T${startTime.replace('+0300', '+03:00')}`);
	const formattedEnd = dayjs(`${today}T${endTime.replace('+0300', '+03:00')}`);

	return baseTime.isAfter(formattedStart) && baseTime.isBefore(formattedEnd);
}

export const checkWebView = () => {
	return typeof window !== 'undefined' && typeof window?.ReactNativeWebView !== 'undefined' && navigator.userAgent.includes('3p-webview');
};

export const isValidURL = (url: string): boolean => {
	try {
		const urlObj = new URL(url);
		return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
	} catch {
		return false;
	}
};

export const sendGenericEmit = (eventName: string, payload: any, emit: any) => {
	emit(SOCKET_EVENTS.EMIT.GENERIC_EVENT, {
		...payload,
		eventName
	});
};

export const getExtension = (url: string = ''): string | undefined => {
	return url?.split('.')?.pop();
};

export const getFileName = (url: string = ''): string | undefined => {
	return url?.split('/')?.pop();
};
